import { Button as AriaButton, Link as AriaLink } from 'react-aria-components';
import { useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconDownload from '@/icons/Download.svg';
import IconError from '@/icons/Error.svg';
import IconFile from '@/icons/File.svg';
import IconTrash from '@/icons/Trash.svg';
import { createBEMClasses } from '@/utils/classname';

import '@/design_system/Button/Button.css';
import './FileCard.css';

type FileCardProps = {
  name: string;
  url?: string;
  size: number;
  error?: boolean;
  loading?: boolean;
  onDelete?: () => void;
  ariaLabel?: string;
  style?: React.CSSProperties;
  showDeletedPlaceholder?: boolean;
};

const { block, element } = createBEMClasses('file-card');

export const getFormattedSize = (size: number) =>
  size < 1024
    ? `${size}B`
    : size < 1024 * 1024
      ? `${(size / 1024).toFixed(0)}KB`
      : `${(size / 1024 / 1024).toFixed(1)}MB`;

const FileCard = ({
  ariaLabel,
  name,
  url,
  size,
  error = false,
  onDelete,
  loading,
  style,
  showDeletedPlaceholder = false,
}: FileCardProps) => {
  const { t } = useLingui();
  const formattedSize = getFormattedSize(size);

  if (showDeletedPlaceholder) {
    return (
      <div className={block({})} aria-label={name}>
        <Stack
          row
          gap="0.5rem"
          alignItems="center"
          className={element('deleted-placeholder', {}, 'text-disabled')}
        >
          <IconTrash />
          <InlineText size="200">
            {t({ id: 'design-system.file-card.deleted', message: 'Deleted file' })}
          </InlineText>
        </Stack>
      </div>
    );
  }

  return (
    <Stack
      row
      gap="0.5rem"
      alignItems="center"
      ariaLabel={ariaLabel}
      className={block({ error, loading })}
      flexWrap="nowrap"
      style={style}
    >
      <Stack row flexWrap="nowrap" gap="0.5rem" alignItems="center" className="text-ellipsis">
        {loading ? <Loader style={{ margin: '2px' }} /> : error ? <IconError /> : <IconFile />}
        {url ? (
          <InlineText ellipsis title={name}>
            {name}
          </InlineText>
        ) : (
          <InlineText ellipsis>{name}</InlineText>
        )}
        {error && <InlineText color="disabled">{formattedSize}</InlineText>}
      </Stack>

      <Stack row gap="0.5rem" flexWrap="nowrap" style={{ marginLeft: 'auto' }}>
        {url && (
          <AriaLink
            href={url}
            download
            className="button button--variant-secondary button--size-small button--icon-only"
            target="_blank"
            aria-label={t({
              id: 'design-system.file-card.download',
              message: `Download file ${name}`,
            })}
          >
            <IconDownload />
          </AriaLink>
        )}
        {onDelete && (
          <AriaButton
            className="button button--variant-secondary-danger button--size-small button--icon-only"
            onPress={onDelete}
            aria-label={t({
              id: 'design-system.file-card.remove',
              message: `Remove file ${name}`,
            })}
          >
            <IconTrash />
          </AriaButton>
        )}
      </Stack>
    </Stack>
  );
};

export default FileCard;
